import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { FollowUpSpec } from '../api/types';
import { useDebounced } from '../components/useDebounced';
import { useProjectApi } from '../projects/useProjectApi';
import { addDays, fromLocal, localDate } from './format';

const presets: { value: NonNullable<FollowUpSpec['preset']>; label: string }[] = [
  { value: 'IN_1_HOUR', label: 'In 1 Stunde' },
  { value: 'THIS_AFTERNOON', label: 'Heute Nachmittag' },
  { value: 'TOMORROW', label: 'Morgen' },
  { value: 'IN_2_DAYS', label: 'In 2 Tagen' },
  { value: 'NEXT_WEEK', label: 'Nächste Woche' },
];

interface Props {
  // onPick wird mit der gewählten Zeitangabe aufgerufen (Voreinstellung, Datum oder freier Text).
  onPick: (spec: FollowUpSpec) => void;
  busy?: boolean;
  label?: string;
}

// FollowUpPicker: Wiedervorlagen mit einem Klick – Voreinstellungen, Datum oder natürliche Sprache.
export function FollowUpPicker({ onPick, busy, label = 'Wiedervorlage' }: Props) {
  const papi = useProjectApi();
  const [mode, setMode] = useState<'presets' | 'date' | 'text'>('presets');
  const [date, setDate] = useState(addDays(localDate(), 1));
  const [time, setTime] = useState('10:00');
  const [text, setText] = useState('');
  const debounced = useDebounced(text.trim(), 350);
  const parsed = useQuery({
    queryKey: ['parse-follow-up', papi.projectId, debounced],
    queryFn: ({ signal }) => papi.parseFollowUp(debounced, signal),
    enabled: mode === 'text' && debounced.length >= 3,
    retry: false,
  });

  return (
    <div className="follow-up-picker" role="group" aria-label={label}>
      <div className="preset-row">
        {presets.map((p) => (
          <button key={p.value} type="button" className="btn" disabled={busy} onClick={() => onPick({ preset: p.value })}>
            {p.label}
          </button>
        ))}
        <button type="button" className={mode === 'date' ? 'btn active' : 'btn'} aria-pressed={mode === 'date'} onClick={() => setMode(mode === 'date' ? 'presets' : 'date')}>
          Datum wählen
        </button>
        <button type="button" className={mode === 'text' ? 'btn active' : 'btn'} aria-pressed={mode === 'text'} onClick={() => setMode(mode === 'text' ? 'presets' : 'text')}>
          Freie Eingabe
        </button>
      </div>
      {mode === 'date' && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            onPick({ due_at: fromLocal(date, time) });
          }}
        >
          <label className="field narrow">
            <span>Datum</span>
            <input type="date" value={date} min={localDate()} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field narrow">
            <span>Uhrzeit</span>
            <input type="time" value={time} step={900} onChange={(e) => setTime(e.target.value)} required />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Übernehmen
          </button>
        </form>
      )}
      {mode === 'text' && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) onPick({ text: text.trim() });
          }}
        >
          <label className="field wide">
            <span>Wann?</span>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="z. B. morgen um 14 Uhr, Montag Vormittag" autoFocus />
          </label>
          <span className="parse-preview" aria-live="polite">
            {parsed.data ? `→ ${parsed.data.label}` : parsed.isError && debounced.length >= 3 ? 'Nicht erkannt' : ''}
          </span>
          <button type="submit" className="btn btn-primary" disabled={busy || !parsed.data}>
            Übernehmen
          </button>
        </form>
      )}
    </div>
  );
}
